import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Button,
  Tabs,
  Tab,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSelector, useDispatch } from "react-redux";
import {
  selectUser,
  updateUserProfile,
  changePassword,
} from "../../store/slices/authSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useQuery } from "@tanstack/react-query";
import useNotification from "../../hooks/common/useNotification";
import usersAPI from "../../services/api/users.api";

const profileSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  middle_name: Yup.string(),
  sur_name: Yup.string().required("Surname is required"),
  phone: Yup.string(),
  date_of_birth: Yup.date(),
  gender: Yup.string().oneOf(
    ["Male", "Female"],
    "Please select a valid gender"
  ),
});

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Password must contain at least one uppercase letter, one lowercase letter, and one number"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const ProfilePage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { showSuccess, showError } = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Fetch current user's full profile to get department (same as employee list)
  const { data: currentUserProfileData } = useQuery({
    queryKey: ["current-user-profile", user?.id],
    queryFn: () => usersAPI.getUserById(user?.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get user with department from profile or fallback to Redux user
  const currentUserProfile = currentUserProfileData?.data || currentUserProfileData;
  const userWithDepartment = currentUserProfile || user;

  const profileFormik = useFormik({
    initialValues: {
      first_name: userWithDepartment?.first_name || user?.first_name || "",
      middle_name: userWithDepartment?.middle_name || user?.middle_name || "",
      sur_name: userWithDepartment?.sur_name || user?.sur_name || "",
      phone: userWithDepartment?.phone_number || user?.phone_number || "",
      date_of_birth: userWithDepartment?.date_of_birth?.split("T")[0] || user?.date_of_birth?.split("T")[0] || "",
      gender: userWithDepartment?.gender || user?.gender || "",
    },
    validationSchema: profileSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await dispatch(updateUserProfile(values)).unwrap();
        showSuccess("Profile updated successfully");
        setEditMode(false);
      } catch (error) {
        showError(error || "Failed to update profile");
      } finally {
        setLoading(false);
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm }) => {
      setPasswordLoading(true);
      try {
        await dispatch(
          changePassword({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          })
        ).unwrap();

        showSuccess("Password changed successfully");
        resetForm();
      } catch (error) {
        showError(error || "Failed to change password");
      } finally {
        setPasswordLoading(false);
      }
    },
  });

  const handleCancelEdit = () => {
    profileFormik.resetForm();
    setEditMode(false);
  };

  const getUserDisplayName = () => {
    const firstName = userWithDepartment?.first_name || user?.first_name || "";
    const surname = userWithDepartment?.sur_name || user?.sur_name || "";
    return firstName && surname
      ? `${firstName} ${surname}`
      : userWithDepartment?.email || user?.email || "User";
  };

  const getUserInitials = () => {
    const firstName = userWithDepartment?.first_name || user?.first_name || "";
    const surname = userWithDepartment?.sur_name || user?.sur_name || "";
    if (firstName && surname) {
      return `${firstName.charAt(0)}${surname.charAt(0)}`.toUpperCase();
    }
    const email = userWithDepartment?.email || user?.email;
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const tabPanels = [
    {
      label: "Personal Info",
      content: (
        <Box component="form" onSubmit={profileFormik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="first_name"
                label="First Name"
                value={profileFormik.values.first_name}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.first_name &&
                  Boolean(profileFormik.errors.first_name)
                }
                helperText={
                  profileFormik.touched.first_name &&
                  profileFormik.errors.first_name
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                name="middle_name"
                label="Middle Name"
                value={profileFormik.values.middle_name}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.middle_name &&
                  Boolean(profileFormik.errors.middle_name)
                }
                helperText={
                  profileFormik.touched.middle_name &&
                  profileFormik.errors.middle_name
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                name="sur_name"
                label="Surname"
                value={profileFormik.values.sur_name}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.sur_name &&
                  Boolean(profileFormik.errors.sur_name)
                }
                helperText={
                  profileFormik.touched.sur_name &&
                  profileFormik.errors.sur_name
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={userWithDepartment?.email || user?.email || ""}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                name="phone"
                label="Phone Number"
                value={profileFormik.values.phone}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.phone &&
                  Boolean(profileFormik.errors.phone)
                }
                helperText={
                  profileFormik.touched.phone && profileFormik.errors.phone
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                name="date_of_birth"
                label="Date of Birth"
                type="date"
                value={profileFormik.values.date_of_birth}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.date_of_birth &&
                  Boolean(profileFormik.errors.date_of_birth)
                }
                helperText={
                  profileFormik.touched.date_of_birth &&
                  profileFormik.errors.date_of_birth
                }
                disabled={!editMode}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                name="gender"
                label="Gender"
                select
                value={profileFormik.values.gender}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                error={
                  profileFormik.touched.gender &&
                  Boolean(profileFormik.errors.gender)
                }
                helperText={
                  profileFormik.touched.gender && profileFormik.errors.gender
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {editMode && (
            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
                disabled={!profileFormik.isValid}
              >
                Save Changes
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      ),
    },
    {
      label: "Work Info",
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemText
                  primary="Employee ID"
                  secondary={userWithDepartment?.id || user?.id || "N/A"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Department"
                  secondary={
                    userWithDepartment?.department ||
                    user?.department ||
                    userWithDepartment?.basicEmployeeData?.department?.department_name ||
                    user?.basicEmployeeData?.department?.department_name ||
                    userWithDepartment?.basicEmployeeData?.department_name ||
                    user?.basicEmployeeData?.department_name ||
                    "Not assigned"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Position"
                  secondary={
                    userWithDepartment?.designation ||
                    userWithDepartment?.basicEmployeeData?.designation ||
                    user?.basicEmployeeData?.job_title || 
                    user?.basicEmployeeData?.designation ||
                    "Not assigned"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Employment Type"
                  secondary={
                    userWithDepartment?.employment_type ||
                    userWithDepartment?.basicEmployeeData?.employment_type ||
                    user?.basicEmployeeData?.employment_type || 
                    "Not specified"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Hire Date"
                  secondary={
                    userWithDepartment?.date_joined ||
                    userWithDepartment?.basicEmployeeData?.date_joined ||
                    user?.basicEmployeeData?.hire_date ||
                    user?.basicEmployeeData?.date_joined
                      ? new Date(
                          userWithDepartment?.date_joined ||
                          userWithDepartment?.basicEmployeeData?.date_joined ||
                          user?.basicEmployeeData?.hire_date ||
                          user?.basicEmployeeData?.date_joined
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Contract End Date"
                  secondary={
                    userWithDepartment?.basicEmployeeData?.contract_end_date ||
                    user?.basicEmployeeData?.contract_end_date
                      ? new Date(
                          userWithDepartment?.basicEmployeeData?.contract_end_date ||
                          user?.basicEmployeeData?.contract_end_date
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Chip
                      label={userWithDepartment?.status || user?.status || "Active"}
                      color={(userWithDepartment?.status || user?.status) === "active" ? "success" : "warning"}
                      size="small"
                    />
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Role"
                  secondary={
                    (userWithDepartment?.roles || user?.roles) && (userWithDepartment?.roles || user?.roles).length > 0
                      ? (userWithDepartment?.roles || user?.roles)
                          .map((role) => role.role_name || role)
                          .join(", ")
                      : "Employee"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Supervisor"
                  secondary={
                    (() => {
                      const supervisor = userWithDepartment?.basicEmployeeData?.supervisor || user?.basicEmployeeData?.supervisor;
                      if (!supervisor) return "Not assigned";
                      // If supervisor is an object, format it as a name
                      if (typeof supervisor === 'object') {
                        const firstName = supervisor.first_name || '';
                        const middleName = supervisor.middle_name || '';
                        const surName = supervisor.sur_name || '';
                        return `${firstName} ${middleName} ${surName}`.trim() || "Not assigned";
                      }
                      // If it's already a string, return it
                      return supervisor;
                    })()
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Work Location"
                  secondary={
                    userWithDepartment?.basicEmployeeData?.work_location ||
                    user?.basicEmployeeData?.work_location || 
                    "Main Office"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Last Login"
                  secondary={
                    userWithDepartment?.last_login ||
                    user?.last_login
                      ? new Date(userWithDepartment?.last_login || user?.last_login).toLocaleString()
                      : "Never"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Created"
                  secondary={
                    userWithDepartment?.created_at ||
                    user?.created_at
                      ? new Date(userWithDepartment?.created_at || user?.created_at).toLocaleDateString()
                      : "N/A"
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Personal Details",
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemText
                  primary="Full Name"
                  secondary={
                    `${userWithDepartment?.first_name || user?.first_name || ""} ${userWithDepartment?.middle_name || user?.middle_name || ""} ${userWithDepartment?.sur_name || user?.sur_name || ""}`.trim() ||
                    "Not provided"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Date of Birth"
                  secondary={
                    userWithDepartment?.date_of_birth ||
                    user?.date_of_birth
                      ? new Date(userWithDepartment?.date_of_birth || user?.date_of_birth).toLocaleDateString()
                      : "Not provided"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Gender"
                  secondary={userWithDepartment?.gender || user?.gender || "Not specified"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Nationality"
                  secondary={userWithDepartment?.bioData?.nationality || user?.bioData?.nationality || "Not specified"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Marital Status"
                  secondary={userWithDepartment?.bioData?.marital_status || user?.bioData?.marital_status || "Not specified"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Blood Group"
                  secondary={userWithDepartment?.bioData?.blood_group || user?.bioData?.blood_group || "Not specified"}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemText
                  primary="Contact Number"
                  secondary={userWithDepartment?.phone_number || user?.phone_number || "Not provided"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Email Address"
                  secondary={userWithDepartment?.email || user?.email || "Not provided"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Emergency Contact"
                  secondary={
                    userWithDepartment?.emergencyContacts?.[0]?.phone_number ||
                    user?.emergencyContacts?.[0]?.phone_number || 
                    "Not provided"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Address"
                  secondary={
                    userWithDepartment?.personalEmployeeData?.address ||
                    user?.personalEmployeeData?.address || 
                    "Not provided"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="City"
                  secondary={userWithDepartment?.personalEmployeeData?.city || user?.personalEmployeeData?.city || "Not provided"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Country"
                  secondary={
                    userWithDepartment?.personalEmployeeData?.country ||
                    user?.personalEmployeeData?.country || 
                    "Not provided"
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Security",
      content: (
        <Box>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Change Password
          </Typography>

          <Box component="form" onSubmit={passwordFormik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  name="currentPassword"
                  label="Current Password"
                  value={passwordFormik.values.currentPassword}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={
                    passwordFormik.touched.currentPassword &&
                    Boolean(passwordFormik.errors.currentPassword)
                  }
                  helperText={
                    passwordFormik.touched.currentPassword &&
                    passwordFormik.errors.currentPassword
                  }
                  inputProps={{ autoComplete: "current-password" }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="password"
                  name="newPassword"
                  label="New Password"
                  value={passwordFormik.values.newPassword}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={
                    passwordFormik.touched.newPassword &&
                    Boolean(passwordFormik.errors.newPassword)
                  }
                  helperText={
                    passwordFormik.touched.newPassword &&
                    passwordFormik.errors.newPassword
                  }
                  inputProps={{ autoComplete: "new-password" }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  label="Confirm New Password"
                  value={passwordFormik.values.confirmPassword}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={
                    passwordFormik.touched.confirmPassword &&
                    Boolean(passwordFormik.errors.confirmPassword)
                  }
                  helperText={
                    passwordFormik.touched.confirmPassword &&
                    passwordFormik.errors.confirmPassword
                  }
                  inputProps={{ autoComplete: "new-password" }}
                  sx={{ mb: 3 }}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={passwordLoading}
                  disabled={!passwordFormik.isValid}
                >
                  Change Password
                </LoadingButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Password Requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="At least 8 characters long" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Include at least one uppercase letter" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Include at least one lowercase letter" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Include at least one number" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Avoid common passwords" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        My Profile
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: "primary.main",
                  fontSize: "3rem",
                  mx: "auto",
                  mb: 2,
                }}
              >
                {getUserInitials()}
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                {getUserDisplayName()}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {userWithDepartment?.designation ||
                  userWithDepartment?.basicEmployeeData?.designation ||
                  user?.basicEmployeeData?.job_title ||
                  user?.position ||
                  "Employee"}
              </Typography>

              <Chip
                label={
                  (userWithDepartment?.roles || user?.roles) && (userWithDepartment?.roles || user?.roles).length > 0
                    ? (userWithDepartment?.roles || user?.roles)[0].role_name || (userWithDepartment?.roles || user?.roles)[0]
                    : "Employee"
                }
                color="primary"
                variant="outlined"
                sx={{ mb: 3 }}
              />

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Contact Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {userWithDepartment?.email || user?.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {userWithDepartment?.phone_number || user?.phone_number || "Not provided"}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Employment Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {userWithDepartment?.department ||
                    user?.department ||
                    userWithDepartment?.basicEmployeeData?.department?.department_name ||
                    user?.basicEmployeeData?.department?.department_name ||
                    userWithDepartment?.basicEmployeeData?.department_name ||
                    user?.basicEmployeeData?.department_name ||
                    "Not assigned"}
                </Typography>
                <Typography variant="body2">
                  Joined{" "}
                  {userWithDepartment?.date_joined ||
                    userWithDepartment?.basicEmployeeData?.date_joined ||
                    user?.basicEmployeeData?.hire_date ||
                    user?.basicEmployeeData?.date_joined ||
                    user?.hire_date
                    ? new Date(
                        userWithDepartment?.date_joined ||
                        userWithDepartment?.basicEmployeeData?.date_joined ||
                        user?.basicEmployeeData?.hire_date ||
                        user?.basicEmployeeData?.date_joined ||
                        user?.hire_date
                      ).toLocaleDateString()
                      : "N/A"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 3,
                  pb: 0,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                >
                  {tabPanels.map((tab, index) => (
                    <Tab key={index} label={tab.label} />
                  ))}
                </Tabs>

                {activeTab === 0 && !editMode && (
                  <Button variant="outlined" onClick={() => setEditMode(true)}>
                    Edit Profile
                  </Button>
                )}
              </Box>

              <Divider />

              <Box sx={{ p: 3 }}>{tabPanels[activeTab]?.content}</Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
